import {
  createRow,
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import type { ApiError } from "../../../../../api";
import { usePersons } from "../../../../../hooks/usePersons";
import { useVariantGenerators } from "../../../../../hooks/useVariantGenerators";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { extractMessage } from "../../../../../mantine/notifications/extractMessage";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { sortBackendDates } from "../../../../../utils/dates/sortBackendDates";
import { MultiConfirmationModal } from "../../../../BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { LoadOrError } from "../../../../LoadOrError/LoadOrError";
import { useFictionalEnergyValueMutations } from "../../hooks/useFictionalEnergyValueMutations";
import type { FictionalEnergyValueResponse } from "../FictionalEnergyValueManager/FictionalEnergyValueManager";
import { getFirstDateColumn } from "./columns/getFirstDateColumn";
import { getGeneratorColumn } from "./columns/getGeneratorColumn";
import { getLastDateColumn } from "./columns/getLastDateColumn";
import { getRedispatchAbregelungColumn } from "./columns/getRedispatchAbregelungColumn";
import { getSonstigeAbschaltungOderDrosselungColumn } from "./columns/getSonstigeAbschaltungOderDrosselungColumn";
import { getSumColumn } from "./columns/getSumColumn";
import { getTechnischeNichtverfuegbarkeitColumn } from "./columns/getTechnischeNichtverfuegbarkeitColumn";
import { updateRowData } from "./columns/updateRowData";

type FictionalEnergyValuesErrors = Record<
  keyof Omit<
    FictionalEnergyValueResponse,
    "id" | "created" | "generatorDisplayName"
  >,
  string | null
>;

const EMPTY_ERRORS: FictionalEnergyValuesErrors = {
  generator: null,
  firstDate: null,
  lastDate: null,
  technischeNichtverfuegbarkeitKwh: null,
  redispatchAbregelungKwh: null,
  sonstigeAbschaltungOderDrosselungKwh: null
};

const INITIAL_ROW_VALUES: Omit<
  FictionalEnergyValueResponse,
  "id" | "created" | "generatorDisplayName"
> = {
  generator: null,
  firstDate: null,
  lastDate: null,
  technischeNichtverfuegbarkeitKwh: 0,
  redispatchAbregelungKwh: 0,
  sonstigeAbschaltungOderDrosselungKwh: 0
};

interface FictionalEnergyValuesTableProps {
  fictionalEnergyValues: Array<FictionalEnergyValueResponse>;
  variantId: number;
}

function FictionalEnergyValuesTable({
  fictionalEnergyValues,
  variantId
}: FictionalEnergyValuesTableProps) {
  const { showErrorToast, showToast } = useToast();
  const {
    data: generators,
    isLoading: generatorsLoading,
    error
  } = useVariantGenerators(variantId);
  const { paragraph6Persons, isLoading: personsLoading } = usePersons({
    variantId
  });

  const { createMutation, editMutation, deleteMutation } =
    useFictionalEnergyValueMutations(variantId);

  const [creationValidationErrors, setCreationValidationErrors] =
    useState<FictionalEnergyValuesErrors>(EMPTY_ERRORS);
  const [editingValidationErrors, setEditingValidationErrors] =
    useState<FictionalEnergyValuesErrors>(EMPTY_ERRORS);
  const [fictionalEnergyForDeletion, setFictionalEnergyForDeletion] = useState<
    Array<FictionalEnergyValueResponse>
  >([]);
  const [selectedGeneratorsForCreating, setSelectedGeneratorsForCreating] =
    useState<Array<number>>([]);

  function validateInputs(values): {
    isValid: boolean;
    errors: FictionalEnergyValuesErrors;
  } {
    let isValid = true;
    const errors = { ...EMPTY_ERRORS };

    if (!values.generator) {
      isValid = false;
      errors.generator = "Bitte wählen Sie einen Erzeuger.";
    }
    if (!values.firstDate) {
      isValid = false;
      errors.firstDate = "Bitte geben Sie ein Startdatum an.";
    }
    if (!values.lastDate) {
      isValid = false;
      errors.lastDate = "Bitte geben Sie ein Enddatum an.";
    }
    if (typeof values.technischeNichtverfuegbarkeitKwh !== "number") {
      isValid = false;
      errors.technischeNichtverfuegbarkeitKwh =
        "Bitte geben Sie die technische Nichtverfügbarkeit in kWh an.";
    }
    if (typeof values.redispatchAbregelungKwh !== "number") {
      isValid = false;
      errors.redispatchAbregelungKwh =
        "Bitte geben Sie die Redispatch-Abregelung in kWh an.";
    }
    if (typeof values.sonstigeAbschaltungOderDrosselungKwh !== "number") {
      isValid = false;
      errors.sonstigeAbschaltungOderDrosselungKwh =
        "Bitte geben Sie die sonstige Abschaltung oder Drosselung in kWh an.";
    }

    return { isValid, errors };
  }

  function validateCreationInputs(): boolean {
    const { isValid, errors } = validateInputs(
      mrtTable.getState().creatingRow?._valuesCache
    );

    setCreationValidationErrors(errors);
    return isValid;
  }

  function validateEditingInputs(): boolean {
    const { isValid, errors } = validateInputs(
      mrtTable.getState().editingRow?._valuesCache
    );

    setEditingValidationErrors(errors);
    return isValid;
  }

  function handleFictionalEnergyCreation({
    exitCreatingMode
  }: {
    exitCreatingMode: () => void;
  }) {
    const isValid = validateCreationInputs();

    const creatingRow = mrtTable.getState().creatingRow;
    if (isValid && creatingRow) {
      const selectedGeneratorIds = selectedGeneratorsForCreating;

      // Create rows for all selected generators
      const createPromises = selectedGeneratorIds.map((generatorId) =>
        createMutation.mutateAsync({
          ...creatingRow._valuesCache,
          generator: generatorId
        })
      );

      Promise.allSettled(createPromises).then((results) => {
        const failedGeneratorIds = selectedGeneratorIds.filter(
          (_, index) => results[index].status === "rejected"
        );
        setSelectedGeneratorsForCreating(failedGeneratorIds);
        if (failedGeneratorIds.length === 0) {
          exitCreatingMode();
        } else {
          const firstFailedGenerator = generators?.find(
            (generator) => generator.id === failedGeneratorIds[0]
          );
          updateRowData({
            table: mrtTable,
            row: creatingRow,
            columnIds: ["generator", "generatorDisplayName"],
            newValues: [
              failedGeneratorIds[0],
              firstFailedGenerator?.name ?? null
            ]
          });
        }

        // Show all errors for failed operations
        results.forEach((result, index) => {
          if (result.status === "rejected") {
            const error = result.reason as ApiError;
            const generatorName = generators?.find(
              (generator) => generator.id === selectedGeneratorIds[index]
            )?.name;
            const errorMessage =
              error.response &&
              error.response.data &&
              typeof error.response.data === "object" &&
              Object.keys(error.response.data).length > 0
                ? Object.values(error.response.data)[0]
                : error;
            showErrorToast(
              <>
                {generatorName ?? selectedGeneratorIds[index]}:{" "}
                {extractMessage(errorMessage)}
              </>
            );
          }
        });

        // Show success count for completed operations
        const successCount = results.filter(
          (r) => r.status === "fulfilled"
        ).length;
        if (successCount > 0) {
          showToast({
            type: "success",
            message: `${successCount} ${successCount > 1 ? "fiktive Strommengen" : "fiktive Strommenge"} erfolgreich erstellt.`
          });
        }
      });
    }
  }

  function handleFictionalEnergyUpdate({
    exitEditingMode
  }: {
    exitEditingMode: () => void;
  }) {
    const isValid = validateEditingInputs();

    const editingRow = mrtTable.getState().editingRow;
    if (isValid && editingRow) {
      editMutation.mutate(
        {
          ...editingRow._valuesCache,
          id: mrtTable.getRow(editingRow.id).getValue("id"),
          created: mrtTable.getRow(editingRow.id).getValue("created")
        },
        {
          onSuccess: () => {
            exitEditingMode();
          },
          onError: (error) => {
            const apiError = error as ApiError;
            if (
              apiError.response &&
              apiError.response.data &&
              typeof apiError.response.data === "object" &&
              Object.keys(apiError.response.data).length > 0
            ) {
              showErrorToast(Object.values(apiError.response.data)[0]);
            } else {
              showErrorToast(error);
            }
          }
        }
      );
    }
  }

  async function handleFictionalEnergyDeletion(): Promise<void> {
    const results = await Promise.allSettled(
      fictionalEnergyForDeletion.map((fev) =>
        deleteMutation.mutateAsync(fev.id)
      )
    );

    // Show all errors for failed operations
    results.forEach((result) => {
      if (result.status === "rejected") {
        const error = result.reason as ApiError;
        if (
          error.response &&
          error.response.data &&
          typeof error.response.data === "object" &&
          Object.keys(error.response.data).length > 0
        ) {
          showErrorToast(Object.values(error.response.data)[0]);
        } else {
          showErrorToast(error);
        }
      }
    });

    const successCount = results.filter((r) => r.status === "fulfilled").length;
    if (successCount > 0) {
      showToast({
        type: "success",
        message: `${successCount} ${successCount > 1 ? "fiktive Strommengen" : "fiktive Strommenge"} gelöscht.`
      });
    }
  }

  const cols: Array<MRT_ColumnDef<FictionalEnergyValueResponse>> =
    useMemo(() => {
      const paragraph6Generators =
        generators?.filter(
          (generator) =>
            paragraph6Persons.filter((person) => person.id === generator.person)
              .length > 0
        ) ?? [];

      return [
        // invisible columns for meta data
        { accessorKey: "id", header: "ID", enableEditing: false },
        {
          accessorKey: "created",
          header: "Erstellt",
          enableEditing: false
        },
        getGeneratorColumn(
          paragraph6Generators ?? [],
          creationValidationErrors.generator,
          editingValidationErrors.generator,
          selectedGeneratorsForCreating,
          (generatorIds) => {
            setSelectedGeneratorsForCreating(generatorIds);
          }
        ),
        getFirstDateColumn(
          creationValidationErrors.firstDate,
          editingValidationErrors.firstDate
        ),
        getLastDateColumn(
          creationValidationErrors.lastDate,
          editingValidationErrors.lastDate
        ),
        getTechnischeNichtverfuegbarkeitColumn(
          creationValidationErrors.technischeNichtverfuegbarkeitKwh,
          editingValidationErrors.technischeNichtverfuegbarkeitKwh
        ),
        getRedispatchAbregelungColumn(
          creationValidationErrors.redispatchAbregelungKwh,
          editingValidationErrors.redispatchAbregelungKwh
        ),
        getSonstigeAbschaltungOderDrosselungColumn(
          creationValidationErrors.sonstigeAbschaltungOderDrosselungKwh,
          editingValidationErrors.sonstigeAbschaltungOderDrosselungKwh
        ),
        getSumColumn()
      ];
    }, [
      creationValidationErrors,
      editingValidationErrors,
      generators,
      paragraph6Persons,
      selectedGeneratorsForCreating
    ]);

  const mrtTable = useMantineReactTable<FictionalEnergyValueResponse>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Es gibt noch keine fiktiven Strommengen",
      initialState: {
        sorting: [{ id: "created", desc: true }],
        showGlobalFilter: true,
        columnVisibility: { id: false, created: false }
      },
      hideBorder: true
    }),
    sortingFns: {
      backendDatesByCreated: (rowA, rowB) =>
        sortBackendDates(rowA.original.created, rowB.original.created)
    },
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Fiktive Strommenge hinzufügen",
        onClick: () =>
          mrtTable.setCreatingRow(createRow(mrtTable, INITIAL_ROW_VALUES))
      },
      delete: {
        onClick: (rowSelection: MRT_RowSelectionState) => {
          setFictionalEnergyForDeletion(
            Object.keys(rowSelection).map(
              (id) =>
                fictionalEnergyValues.find(
                  (fev) => fev.id === id
                ) as FictionalEnergyValueResponse
            )
          );
        }
      }
    }),
    onCreatingRowSave: handleFictionalEnergyCreation,
    onCreatingRowCancel: () => setSelectedGeneratorsForCreating([]),
    positionCreatingRow: "bottom",
    onEditingRowSave: handleFictionalEnergyUpdate,
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: { text: "Bearbeiten" },
        delete: {
          text: "Löschen",
          onClick: (row) => setFictionalEnergyForDeletion([row.original])
        }
      }
    }),
    enableSorting: false,
    columns: cols,
    data: fictionalEnergyValues
  });

  return (
    <LoadOrError
      error={error}
      loading={generatorsLoading || personsLoading}
      loadingMessage="Erzeugerdaten werden geladen ..."
    >
      <MantineReactTable table={mrtTable} />
      <MultiConfirmationModal
        actionName="löschen"
        actionObjects={fictionalEnergyForDeletion.map(
          (fev) =>
            `${fev.generatorDisplayName} (${fev.firstDate} – ${fev.lastDate})`
        )}
        confirmationText="Möchten Sie die folgenden Fiktiven Strommengen löschen? Dieser Schritt kann nicht rückgängig gemacht werden."
        headerText="Fiktive Strommengen löschen?"
        isModalOpen={fictionalEnergyForDeletion.length > 0}
        objectName="Fiktive Strommenge"
        toggleModal={() => {
          setFictionalEnergyForDeletion([]);
          mrtTable.setRowSelection({});
        }}
        onAction={handleFictionalEnergyDeletion}
      />
    </LoadOrError>
  );
}

export { FictionalEnergyValuesTable, FictionalEnergyValuesTableProps };
